/* ==========================================================================
   Xenon home page sections. Loaded as type="module" from each section that
   needs it, so it runs once however many of them are on the page.
   Custom elements, so the theme editor's section re-render upgrades them.
   ========================================================================== */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------------
   Hero slider: scroll-snap track, dots, arrows, autoplay that pauses while
   the shopper is on it and never runs under reduced motion.

   Slide videos (desktop and mobile each optional) sit in <template>s and are
   mounted ONLY for the active slide at the current breakpoint. Off-screen
   slides are paused, a video slide holds the autoplay until its clip ends and
   then moves on, and the whole hero pauses once it leaves the viewport.

   Slides after the first arrive with their image parked in data-srcset /
   data-src ([data-defer], see the section). The next one is fetched once the
   first has painted and the page is idle, so it never shares the connection
   with the LCP image; the rest one turn before autoplay reaches them, as a
   swipe uncovers them, or (neighbours) when a mouse or keyboard arrives on
   the hero. Autoplay never moves onto a slide whose picture has not loaded
   yet: on a slow line it waits a turn. It also waits while the hero is off
   screen, so nobody scrolled down to the shelves downloads banners.
   -------------------------------------------------------------------------- */
const saveData = () => !!(navigator.connection && navigator.connection.saveData);

class XenonHomeHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.count = this.slides.length;

    this.index = 0;
    this.paused = false;
    this.visible = true;
    this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion();
    this.videoAllowed = !reducedMotion() && !saveData();
    this.mq = window.matchMedia('(min-width: 768px)');

    const first = this.slides[0]?.querySelector('img');
    this.firstLoaded = new Promise((resolve) => {
      if (!first || first.complete) return resolve();
      first.addEventListener('load', resolve, { once: true });
      first.addEventListener('error', resolve, { once: true });
    });

    this.onScroll = () => {
      const pos = this.track.scrollLeft / Math.max(1, this.track.clientWidth);
      /* Both slides that are partly in view during a swipe or a slide change. */
      this.hydrate(this.slides[Math.floor(pos)], 'high');
      this.hydrate(this.slides[Math.ceil(pos)], 'high');
      const i = Math.round(pos);
      if (i !== this.index) {
        this.index = i;
        this.syncDots();
        this.activate();
      }
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));
    this.addEventListener(
      'touchstart',
      () => {
        this.paused = true;
        clearTimeout(this.touchTimer);
        this.touchTimer = setTimeout(() => (this.paused = false), 8000);
      },
      { passive: true }
    );

    /* A mouse over the hero or keyboard focus in it: the arrows and dots are
       about to be used, so fetch both neighbours. Not on touch: a thumb on the
       hero is usually scrolling the page, and a sideways swipe reaches
       onScroll. Never before the first slide has its own picture. */
    const wake = () =>
      this.firstLoaded.then(() => {
        this.hydrate(this.slides[(this.index + 1) % this.count]);
        this.hydrate(this.slides[(this.index - 1 + this.count) % this.count]);
      });
    this.addEventListener('mouseenter', wake, { once: true });
    this.addEventListener('focusin', wake, { once: true });

    /* Breakpoint flip: the other file belongs on screen now. */
    this.onMq = () => {
      this.slides.forEach((slide) => this.unmount(slide));
      this.activate();
    };
    this.mq.addEventListener?.('change', this.onMq);

    this.io = new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting);
      this.activate();
    });
    this.io.observe(this);

    this.setupDrag();
    this.syncDots();
    this.activate();
    this.start();
    this.queueNext();
    /* A position the browser restored, or a swipe made before this ran. */
    if (this.track.scrollLeft > 0) this.onScroll();
  }

  /* After the first slide's image has loaded and been painted, and the main
     thread has a quiet moment: the slide autoplay shows next. */
  queueNext() {
    if (!this.track.querySelector('[data-defer]')) return;
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
    this.firstLoaded.then(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          idle(() => this.hydrate(this.slides[(this.index + 1) % this.count], 'low'), { timeout: 4000 })
        )
      )
    );
  }

  /* Moves a deferred slide's files into place. priority: 'high' when it is
     about to be on screen, 'low' for the quiet fetch ahead of autoplay, none
     to leave it to the browser (which raises an image that comes into view;
     an explicit 'low' would stop that). */
  hydrate(slide, priority) {
    if (!slide || !slide.hasAttribute('data-defer')) return;
    slide.removeAttribute('data-defer');
    const img = slide.querySelector('img[data-src]');
    if (img) {
      if (priority) img.setAttribute('fetchpriority', priority);
      else img.removeAttribute('fetchpriority');
      img.loading = 'eager';
    }
    slide.querySelectorAll('source[data-srcset]').forEach((source) => {
      source.srcset = source.dataset.srcset;
      source.removeAttribute('data-srcset');
    });
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }

  /* A broken file counts as ready (complete), so it cannot stall autoplay. */
  ready(slide) {
    const img = slide?.querySelector('img');
    return !img || (!slide.hasAttribute('data-defer') && img.complete);
  }

  /* Mouse drag; touch already scrolls the track natively. While dragging,
     snapping is off and the track follows the pointer. On release it lands on
     the next or previous slide once the drag passes a small threshold (else
     back where it started), and only re-enables snapping after that scroll
     settles, so the snap cannot pull it back mid-flight. A drag is never also
     a click on the slide's link. */
  setupDrag() {
    if (this.count < 2) return;
    const track = this.track;
    let start = null;
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      start = { x: event.clientX, left: track.scrollLeft, id: event.pointerId, moved: false };
    });
    track.addEventListener('pointermove', (event) => {
      if (!start || event.pointerId !== start.id) return;
      const dx = event.clientX - start.x;
      if (!start.moved) {
        if (Math.abs(dx) < 6) return;
        start.moved = true;
        track.setPointerCapture(start.id);
        clearTimeout(this.settleTimer);
        this.classList.add('is-dragging');
      }
      track.scrollLeft = start.left - dx;
    });
    const end = (event) => {
      if (!start || event.pointerId !== start.id) return;
      const drag = start;
      start = null;
      if (!drag.moved) return;
      const dx = event.clientX - drag.x;
      const width = track.clientWidth;
      let target = Math.round(drag.left / Math.max(1, width));
      if (Math.abs(dx) > Math.min(80, width * 0.12)) target += dx < 0 ? 1 : -1;
      target = Math.max(0, Math.min(this.count - 1, target));
      this.dragged = true;
      setTimeout(() => (this.dragged = false), 0);
      this.go(target, true);
      const settle = () => {
        clearTimeout(this.settleTimer);
        track.removeEventListener('scrollend', settle);
        this.classList.remove('is-dragging');
      };
      track.addEventListener('scrollend', settle, { once: true });
      this.settleTimer = setTimeout(settle, 700);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener(
      'click',
      (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this.settleTimer);
    clearTimeout(this.touchTimer);
    this.io?.disconnect();
    this.mq?.removeEventListener?.('change', this.onMq);
  }

  start() {
    clearInterval(this.timer);
    if (!this.autoplay || this.count < 2) return;
    this.timer = setInterval(() => {
      if (this.paused || document.hidden || !this.visible) return;
      /* A clip that is still running owns the slide; 'ended' advances. */
      const video = this.videoOf(this.slides[this.index]);
      if (video && !video.ended && !video.error) return;
      const next = this.slides[(this.index + 1) % this.count];
      if (!this.ready(next)) {
        /* Not while the slide on screen is itself still loading (slide 1 on
           a slow line): queueNext fetches the next one after it. */
        if (this.ready(this.slides[this.index])) this.hydrate(next, 'high');
        return;
      }
      this.go(this.index + 1, false);
    }, this.interval);
  }

  /* restart: the next slide gets a full interval. Arrows, dots and a clip
     that has just ended all restart; only the timer's own tick does not. */
  go(i, restart) {
    this.index = (i + this.count) % this.count;
    this.hydrate(this.slides[this.index], 'high');
    this.hydrate(this.slides[(this.index + 1) % this.count], 'low');
    this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
    this.syncDots();
    this.activate();
    if (restart) this.start();
  }

  syncDots() {
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
  }

  videoOf(slide) {
    return slide ? slide.querySelector('video.xh-hero__video') : null;
  }

  unmount(slide) {
    const video = this.videoOf(slide);
    if (!video) return;
    video.pause();
    video.removeAttribute('src');
    video.remove();
    delete slide.dataset.videoKey;
  }

  mount(slide) {
    const key = this.mq.matches ? 'desktop' : 'mobile';
    if (slide.dataset.videoKey === key) return this.videoOf(slide);
    this.unmount(slide);
    const tpl = slide.querySelector(`template[data-video="${key}"]`);
    if (!tpl) return null;
    const video = tpl.content.firstElementChild.cloneNode(true);
    /* Set as properties BEFORE insertion, or older WebKit goes fullscreen. */
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    /* With several slides the clip plays once and hands over; alone, it loops. */
    video.loop = !this.autoplay || this.count < 2;
    video.addEventListener('playing', () => video.classList.add('is-playing'));
    /* The interval kept ticking under the clip, so without the restart the
       slide after it could be moved on a moment after it arrived. */
    video.addEventListener('ended', () => {
      if (!this.autoplay || this.count < 2 || this.paused || this.slides[this.index] !== slide) return;
      /* Like the timer, not onto a picture still loading; its next tick moves on. */
      const next = this.slides[(this.index + 1) % this.count];
      if (!this.ready(next)) return this.hydrate(next, 'high');
      this.go(this.index + 1, true);
    });
    tpl.parentNode.insertBefore(video, tpl);
    slide.dataset.videoKey = key;
    return video;
  }

  activate() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      if (!active || !this.visible || !this.videoAllowed) {
        this.videoOf(slide)?.pause();
        return;
      }
      const video = this.mount(slide);
      if (!video) return;
      if (video.ended) video.currentTime = 0;
      const playing = video.play();
      if (playing && playing.catch) playing.catch(() => {});
    });
  }
}
if (!customElements.get('xenon-home-hero')) customElements.define('xenon-home-hero', XenonHomeHero);

/* --------------------------------------------------------------------------
   Launch video. The video lives in <template>s and is mounted only when the
   section comes near the viewport — a homepage must not pull megabytes of
   video for a section most visitors never scroll to. Desktop and mobile
   each get their own file; only the one that matches is ever mounted.
   -------------------------------------------------------------------------- */
class XenonLaunchVideo extends HTMLElement {
  connectedCallback() {
    if (this.io) return;
    this.mq = window.matchMedia('(min-width: 768px)');
    this.io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          this.mount();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this.io.observe(this);
    this.querySelector('[data-mute]')?.addEventListener('click', () => this.toggleSound());
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  mount() {
    const pick = this.mq.matches ? 'desktop' : 'mobile';
    const tpl =
      this.querySelector(`template[data-video="${pick}"]`) || this.querySelector('template[data-video]');
    if (!tpl) return;
    const node = tpl.content.firstElementChild?.cloneNode(true);
    if (!node) return;
    if (node.tagName === 'VIDEO') {
      node.muted = true;
      node.playsInline = true;
      node.setAttribute('playsinline', '');
      node.loop = true;
      node.autoplay = true;
    }
    this.querySelector('[data-video-slot]').appendChild(node);
    this.video = node.tagName === 'VIDEO' ? node : null;
    if (this.video) {
      this.classList.add('has-video');
      this.video.play?.().catch(() => {});
    }
  }

  toggleSound() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.classList.toggle('is-unmuted', !this.video.muted);
    const btn = this.querySelector('[data-mute]');
    btn?.setAttribute('aria-label', this.video.muted ? 'Sesi aç' : 'Sesi kapat');
  }
}
if (!customElements.get('xenon-launch-video')) customElements.define('xenon-launch-video', XenonLaunchVideo);

/* --------------------------------------------------------------------------
   Launch details behind dirty glass that the launch product, a window
   cleaning robot, wipes clean. The robot photo (section setting) works the
   pane like the real one: left to right, a row down, back, spraying water
   ahead of itself, and every stretch it passes is clean. Clean, it rests,
   the fog rolls back in and it starts over.

   The fog is one backdrop-filter layer clipped to the part still dirty, so
   the text under it stays real, selectable text. Built only when a robot
   image is set and never under reduced motion; it runs only while the
   details are on screen.
   -------------------------------------------------------------------------- */
const GLASS_FOG_IN = 900;
const GLASS_ROBOT_IN = 450;
const GLASS_SETTLE = 250;
const GLASS_TURN = 420;
const GLASS_CLEAN = 10500;
const GLASS_HOLD = 3400;
const glassEase = (p) => 0.5 - Math.cos(Math.PI * p) / 2;
const clamp01 = (v) => Math.min(1, Math.max(0, v));

class XenonGlassClean extends HTMLElement {
  connectedCallback() {
    if (this.glass || !this.dataset.src || reducedMotion()) return;
    if (!CSS.supports('backdrop-filter', 'blur(1px)') && !CSS.supports('-webkit-backdrop-filter', 'blur(1px)')) return;

    this.glass = document.createElement('div');
    this.glass.className = 'xh-clean__glass';
    this.glass.setAttribute('aria-hidden', 'true');
    this.fog = document.createElement('div');
    this.fog.className = 'xh-clean__fog';
    this.bot = document.createElement('div');
    this.bot.className = 'xh-clean__bot';
    this.bot.innerHTML = '<span class="xh-clean__body"></span><span class="xh-clean__jets"><i></i><i></i><i></i></span>';
    this.robot = new Image();
    this.robot.alt = '';
    this.robot.sizes = '80px';
    if (this.dataset.srcset) this.robot.srcset = this.dataset.srcset;
    this.robot.src = this.dataset.src;
    this.bot.firstChild.append(this.robot);
    this.glass.append(this.fog, this.bot);

    this.t = 0;
    this.raf = 0;
    this.frame = this.frame.bind(this);
    this.io = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        this.toggle();
      },
      { threshold: 0.3 }
    );
    this.ro = new ResizeObserver(() => this.measure());

    const glass = this.glass;
    this.robot
      .decode()
      .then(() => {
        if (this.glass !== glass) return;
        this.append(glass);
        this.measure();
        this.ro.observe(glass);
        this.io.observe(this);
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.io?.disconnect();
    this.ro?.disconnect();
    this.glass?.remove();
    this.glass = null;
  }

  measure() {
    const w = this.glass.clientWidth;
    const h = this.glass.clientHeight;
    if (!w || !h) return;
    this.w = w;
    this.h = h;
    // Rows the height of the robot, which is the strip it cleans.
    this.rows = Math.max(2, Math.round(h / (w < 420 ? 54 : 68)));
    this.rowH = h / this.rows;
    this.size = Math.round(this.rowH);
    this.bot.style.width = `${this.size}px`;
    this.bot.style.height = `${this.size}px`;
    this.rowDur = (GLASS_CLEAN - (this.rows - 1) * GLASS_TURN) / this.rows;
    this.cycle = GLASS_FOG_IN + GLASS_ROBOT_IN + GLASS_SETTLE + GLASS_CLEAN + GLASS_HOLD;
  }

  toggle() {
    if (this.visible && this.w && !this.raf) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.frame);
    } else if (!this.visible && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  frame(now) {
    // Capped step: a background tab or a long frame resumes, never jumps.
    this.t += Math.min(now - this.last, 50);
    this.last = now;
    if (this.t >= this.cycle) {
      this.t -= this.cycle;
      this.fog.replaceChildren();
      this.sparkled = false;
    }
    this.render(now);
    this.raf = requestAnimationFrame(this.frame);
  }

  render(now) {
    const { w, h, rows, rowH, size: s } = this;
    const start = GLASS_FOG_IN + GLASS_ROBOT_IN + GLASS_SETTLE;
    let t = this.t;
    let fog = 1;
    let clip = 'none';
    let robot = 1;
    let x = s / 2;
    let y = rowH / 2;
    let spray = 0;

    // The glass gets dirty first; only then does the robot show up.
    if (t < GLASS_FOG_IN) {
      fog = glassEase(t / GLASS_FOG_IN);
      robot = 0;
    } else if (t < start) {
      robot = clamp01((t - GLASS_FOG_IN) / GLASS_ROBOT_IN);
    } else if (t >= start && t < start + GLASS_CLEAN) {
      t -= start;
      // Past the last row (rounding): everything is clean.
      clip = `inset(${h}px 0 0 0)`;
      x = (rows - 1) % 2 === 0 ? w - s / 2 : s / 2;
      y = h - rowH / 2;
      for (let i = 0; i < rows; i++) {
        const top = i * rowH;
        const bottom = top + rowH;
        const ltr = i % 2 === 0;
        if (t < this.rowDur) {
          const p = glassEase(t / this.rowDur);
          const edge = ltr ? p * w : w - p * w;
          x = ltr ? s / 2 + p * (w - s) : w - s / 2 - p * (w - s);
          y = top + rowH / 2;
          clip = ltr
            ? `polygon(${edge}px ${top}px, ${w}px ${top}px, ${w}px ${h}px, 0 ${h}px, 0 ${bottom}px, ${edge}px ${bottom}px)`
            : `polygon(0 ${top}px, ${edge}px ${top}px, ${edge}px ${bottom}px, ${w}px ${bottom}px, ${w}px ${h}px, 0 ${h}px)`;
          // Spray while there is glass ahead; the jets stop short of the edge.
          if ((ltr ? w - s / 2 - x : x - s / 2) > s * 0.4) spray = ltr ? 1 : -1;
          if (this.row !== i) {
            this.row = i;
            this.sweep(top);
          }
          break;
        }
        t -= this.rowDur;
        if (i < rows - 1 && t < GLASS_TURN) {
          x = ltr ? w - s / 2 : s / 2;
          y = top + rowH / 2 + glassEase(t / GLASS_TURN) * rowH;
          clip = `inset(${bottom}px 0 0 0)`;
          break;
        }
        t -= GLASS_TURN;
      }
    } else if (t >= start + GLASS_CLEAN) {
      fog = 0;
      x = (rows - 1) % 2 === 0 ? w - s / 2 : s / 2;
      y = h - rowH / 2;
      robot = 1 - clamp01((t - start - GLASS_CLEAN - 700) / 400);
      if (!this.sparkled) {
        this.sparkled = true;
        this.row = -1;
        this.sparkle();
      }
    }

    if (fog !== this.fogNow) {
      this.fogNow = fog;
      this.fog.style.opacity = fog;
    }
    if (clip !== this.clipNow) {
      this.clipNow = clip;
      this.fog.style.clipPath = clip;
      this.fog.style.webkitClipPath = clip;
    }
    this.bot.style.opacity = robot;
    this.bot.style.transform = `translate3d(${x - s / 2}px, ${y - s / 2}px, 0)`;
    if (spray !== this.sprayNow) {
      this.sprayNow = spray;
      this.bot.classList.toggle('is-spraying', spray !== 0);
      if (spray) this.bot.classList.toggle('is-rtl', spray < 0);
    }
    if (spray && now - (this.sprayed || 0) > 70) {
      this.sprayed = now;
      this.spray(x, y, spray);
    }
  }

  // Water from the robot's leading edge in three directions, like the
  // product's 3-way spray. The drops sit on the fog, so they disappear
  // exactly as the robot wipes over them.
  spray(cx, cy, dir) {
    const s = this.size;
    const fx = cx + dir * s * 0.49;
    for (const base of [-0.35, 0, 0.35, 0]) {
      if (Math.random() < 0.25) continue;
      const a = base + (Math.random() - 0.5) * 0.3;
      const d = s * (0.3 + Math.random() * 1.9);
      const dx = Math.cos(a) * d * dir;
      const dy = Math.sin(a) * d;
      const lx = fx + dx;
      const ly = cy + dy;
      if (lx < 3 || lx > this.w - 3 || ly < 3 || ly > this.h - 3) continue;
      const r = s * (0.08 + Math.random() * 0.1);
      const drop = document.createElement('i');
      drop.className = 'xh-clean__drop';
      drop.style.cssText = `left:${lx}px;top:${ly}px;width:${r}px;height:${r}px;margin:${-r / 2}px 0 0 ${-r / 2}px`;
      this.fog.append(drop);
      drop.animate(
        [
          { transform: `translate(${-dx}px, ${-dy}px) scale(0.3)`, opacity: 0 },
          { transform: 'none', opacity: 1 },
        ],
        { duration: 160 + d * 1.5, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' }
      );
    }
    if (Math.random() < 0.5) {
      const m = s * 0.9;
      const mist = document.createElement('i');
      mist.className = 'xh-clean__mist';
      mist.style.cssText = `left:${fx + dir * m * 0.35 - m / 2}px;top:${cy - m / 2}px;width:${m}px;height:${m}px`;
      this.fog.append(mist);
      mist.animate(
        [
          { transform: 'scale(0.5)', opacity: 0.6 },
          { transform: 'scale(1.3)', opacity: 0 },
        ],
        { duration: 520, easing: 'ease-out', fill: 'forwards' }
      ).onfinish = () => mist.remove();
    }
  }

  // Drops on rows already wiped are clipped away; drop them from the DOM.
  sweep(top) {
    for (const drop of this.fog.querySelectorAll('.xh-clean__drop')) {
      if (parseFloat(drop.style.top) < top - 2) drop.remove();
    }
  }

  sparkle() {
    for (let n = 0; n < 3; n++) {
      const spark = document.createElement('i');
      spark.className = 'xh-clean__spark';
      spark.style.left = `${12 + Math.random() * 76}%`;
      spark.style.top = `${15 + Math.random() * 70}%`;
      this.glass.append(spark);
      spark.animate(
        [
          { transform: 'scale(0) rotate(0deg)', opacity: 0 },
          { transform: 'scale(1) rotate(45deg)', opacity: 1, offset: 0.45 },
          { transform: 'scale(0) rotate(90deg)', opacity: 0 },
        ],
        { duration: 900, delay: n * 180, easing: 'ease-out', fill: 'both' }
      ).onfinish = () => spark.remove();
    }
  }
}
if (!customElements.get('xenon-glass-clean')) customElements.define('xenon-glass-clean', XenonGlassClean);

/* --------------------------------------------------------------------------
   "LANSMAN ÜRÜNLERİ · LANSMAN ÜRÜNLERİ · …" along the top and bottom bands.
   Whole words only, as many as fit; the space left over goes between them
   (justify), never into the letters. Same rule as the card launch frame in
   card-product-xenon.js.
   -------------------------------------------------------------------------- */
class XenonLaunchRun extends HTMLElement {
  connectedCallback() {
    this.word = this.dataset.text || 'LANSMAN ÜRÜNLERİ';
    this.layout();
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(this);
    document.fonts?.ready.then(() => this.layout());
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  layout() {
    const width = this.clientWidth - parseFloat(getComputedStyle(this).paddingLeft) * 2;
    if (width <= 0) return;
    if (!this.probe) {
      this.probe = document.createElement('span');
      this.probe.textContent = this.word;
      this.probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap';
      this.appendChild(this.probe);
    }
    const word = this.probe.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this).fontSize) * 2.4;
    const count = Math.max(1, Math.floor((width + gap) / (word + gap)));
    if (count === this.count) return;
    this.count = count;
    const parts = [];
    for (let i = 0; i < count; i++) {
      if (i) parts.push('<i></i>');
      parts.push(`<span>${this.word}</span>`);
    }
    this.innerHTML = parts.join('');
    this.probe = null;
    this.style.justifyContent = count === 1 ? 'center' : 'space-between';
  }
}
if (!customElements.get('xenon-launch-run')) customElements.define('xenon-launch-run', XenonLaunchRun);

/* --------------------------------------------------------------------------
   Spare parts: device chips filter the list. There is no "all" chip; the
   first device starts selected (the section renders it that way). Items
   without a device show under every chip.
   -------------------------------------------------------------------------- */
class XenonSpares extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-group]')];
    this.chips = [...this.querySelectorAll('[data-chip]')];
    this.chips.forEach((chip) => chip.addEventListener('click', () => this.filter(chip.dataset.chip)));
    const start = this.chips.find((chip) => chip.getAttribute('aria-pressed') === 'true') || this.chips[0];
    if (start) this.filter(start.dataset.chip);
  }

  filter(group) {
    this.items.forEach((el) => {
      el.hidden = el.dataset.group !== '' && el.dataset.group !== group;
    });
    this.chips.forEach((chip) => chip.setAttribute('aria-pressed', chip.dataset.chip === group ? 'true' : 'false'));
  }
}
if (!customElements.get('xenon-spares')) customElements.define('xenon-spares', XenonSpares);

/* --------------------------------------------------------------------------
   "Diğer setleri gör": opens a dialog and fills it with the category's other
   sets, rendered server-side by sections/xenon-sets-popup through the Section
   Rendering API (a collection URL, or a search for hand-picked ids). Fetched
   on first open only and cached per URL; the set the shopper came from is
   dropped from the list.
   -------------------------------------------------------------------------- */
class XenonHomeSets extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-xh-dialog]');
    if (!this.dialog || this._bound) return;
    this._bound = true;
    this.body = this.dialog.querySelector('[data-xh-dialog-body]');
    this.titleEl = this.dialog.querySelector('[data-xh-dialog-title]');
    this.cache = new Map();

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-xh-others]');
      if (opener) this.open(opener);
    });
    /* Start the request on intent rather than on click: pointing at, focusing
       or touching the button is usually a few hundred ms ahead of the click. */
    const prefetch = (event) => {
      const opener = event.target.closest?.('[data-xh-others]');
      if (opener) this.prefetch(opener.dataset.xhOthers);
    };
    this.addEventListener('pointerover', prefetch);
    this.addEventListener('focusin', prefetch);
    this.addEventListener('touchstart', prefetch, { passive: true });
    this.dialog.querySelector('[data-xh-dialog-close]')?.addEventListener('click', () => this.dialog.close());
    /* A click that lands on the dialog element itself is a click on the backdrop. */
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    /* Picking a set in the popup swaps it onto the home page card. Quick add
       (xcard-quick-add) stops propagation, so it never reaches this. */
    this.body.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      const link = event.target.closest('a[href]');
      const column = event.target.closest('[data-product-id]');
      if (!link || !column || !link.pathname.includes('/products/')) return;
      event.preventDefault();
      this.choose(column, link);
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.removeProperty('overflow');
      this.opener?.focus({ preventScroll: true });
    });
  }

  open(opener) {
    this.opener = opener;
    this.titleEl.textContent = opener.dataset.title || 'Setler';
    /* Two rows of card outlines: enough to open the sheet at the height it
       will have once the cards are in. */
    const skeleton =
      '<div class="xh-pop__skel" aria-hidden="true">' +
      '<span class="xh-pop__skel-img"></span><span class="xh-pop__skel-thumbs"></span>' +
      '<span></span><span class="xh-pop__skel-short"></span>' +
      '<span class="xh-pop__skel-chip"></span><span class="xh-pop__skel-price"></span>' +
      '</div>';
    this.body.innerHTML =
      '<p class="visually-hidden" role="status">Setler yükleniyor</p>' +
      '<div class="xh-pop__grid" aria-busy="true">' + skeleton.repeat(8) + '</div>';
    this.body.scrollTop = 0;
    document.documentElement.style.overflow = 'hidden';
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.load(opener);
  }

  async choose(column, link) {
    const card = this.opener?.closest('.xh-set');
    if (!card || column.classList.contains('is-choosing')) return;
    column.classList.add('is-choosing');
    const handle = link.pathname.split('/products/')[1].split('/')[0];
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${handle}?section_id=xenon-home-set-card`);
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-xh-set-card]');
      const freshLink = fresh?.querySelector('.xh-set__link');
      if (!freshLink) throw new Error('empty');

      /* Keep this card's own category pill and buttons; swap everything else
         (fan layers, both links, the component strip). */
      const pill = card.querySelector('.xh-set__cat');
      const actions = card.querySelector('.xh-set__actions');
      [...card.children].forEach((el) => {
        if (el !== actions) el.remove();
      });
      [...fresh.children].forEach((el) => card.insertBefore(el, actions));
      if (pill) freshLink.querySelector('.xh-set__top')?.prepend(pill);

      /* The add button now adds the new set; a sold-out one shows "Seti
         incele" in its place. */
      const add = card.querySelector('.xh-set__add');
      const view = card.querySelector('.xh-set__view');
      const available = freshLink.dataset.available === 'true' && !!freshLink.dataset.variantId;
      if (add) {
        add.dataset.variantId = freshLink.dataset.variantId || '';
        add.dataset.productTitle = freshLink.dataset.title || '';
        if (typeof add.setState === 'function') add.setState('idle');
        add.hidden = !available;
      }
      if (view) {
        view.href = freshLink.getAttribute('href');
        view.hidden = available;
      }
      this.opener.dataset.exclude = freshLink.dataset.setId;

      this.dialog.close();
      card.classList.remove('is-swapped');
      void card.offsetWidth;
      card.classList.add('is-swapped');
      card.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
    } catch (error) {
      /* Could not build the card: fall back to what the link always did. */
      window.location.href = link.href;
    } finally {
      column.classList.remove('is-choosing');
    }
  }

  /* The popup HTML and the card stylesheet it needs, one request per URL for
     the page's lifetime. A failed request leaves the cache so the next try
     fetches again. */
  fetchPop(url) {
    if (!this.cache.has(url)) {
      const assets = window.FoxTheme?.utils?.loadCardAssets?.();
      const html = fetch(url).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      });
      const request = Promise.all([html, assets]).then(([text]) => text);
      request.catch(() => this.cache.delete(url));
      this.cache.set(url, request);
    }
    return this.cache.get(url);
  }

  prefetch(url) {
    if (url) this.fetchPop(url).catch(() => {});
  }

  async load(opener) {
    const url = opener.dataset.xhOthers;
    try {
      const text = await this.fetchPop(url);
      if (this.opener !== opener || !this.dialog.open) return;

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const pop = doc.querySelector('[data-xh-pop]');
      pop?.querySelectorAll(`[data-product-id="${opener.dataset.exclude}"]`).forEach((el) => el.remove());
      const count = pop ? pop.querySelectorAll('[data-product-id]').length : 0;
      if (!count) throw new Error('empty');
      this.body.replaceChildren(...pop.childNodes);
    } catch (error) {
      const all = opener.dataset.all;
      if (error.message === 'empty') {
        this.body.innerHTML = '<p class="xh-pop__state">Bu kategoride başka set yok.</p>';
        return;
      }
      this.cache.delete(url);
      this.body.innerHTML =
        '<p class="xh-pop__state">Setler şu an yüklenemedi.' +
        (all ? ` <a href="${all}">Tüm setleri gör</a>` : '') +
        '</p>';
    }
  }
}
if (!customElements.get('xenon-home-sets')) customElements.define('xenon-home-sets', XenonHomeSets);
